import { createStyles, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import * as React from "react";

const styles = ({ breakpoints, palette, shape, spacing, typography }: Theme) =>
  createStyles({
    root: {
      ...typography.body2,
      color: palette.text.primary,
      wordBreak: "break-word",
      "& .anchor-link": {
        marginTop: -96, // Offset for the anchor.
        position: "absolute",
      },
      "& pre": {
        margin: spacing(3, "auto"),
        padding: spacing(2),
        backgroundColor: "#272c34",
        direction: "ltr",
        borderRadius: shape.borderRadius,
        overflow: "auto",
        WebkitOverflowScrolling: "touch", // iOS momentum scrolling.
        maxWidth: "calc(100vw - 32px)",
        [breakpoints.up("md")]: {
          maxWidth: "calc(100vw - 32px - 16px)",
        },
      },
      // inline code
      "& code": {
        direction: "ltr",
        lineHeight: 1.4,
        display: "inline-block",
        fontFamily: 'Consolas, "Liberation Mono", Menlo, Courier, monospace',
        WebkitFontSmoothing: "subpixel-antialiased",
        padding: "0 3px",
        color: palette.text.primary,
        backgroundColor:
          palette.type === "light"
            ? "rgba(255, 229, 100, 0.2)"
            : "rgba(255, 229, 100, 0.2)",
        fontSize: ".85em",
        borderRadius: 2,
      },
      '& code[class*="language-"]': {
        backgroundColor: "#272c34",
        color: "#fff",
        // Avoid layout jump after hydration (style injected by prism)
        lineHeight: 1.5,
      },
      // code blocks
      "& pre code": {
        fontSize: ".9em",
      },
      "& .token.operator": {
        background: "transparent",
      },
      "& h1": {
        ...typography.h4,
        margin: "16px 0",
      },
      "& .description": {
        ...typography.h5,
        margin: "0 0 24px",
      },
      "& h2": {
        ...typography.h5,
        margin: "0 0 16px",
        "&:not(:first-child)": {
          marginTop: 40,
        },
      },
      "& h3": {
        ...typography.h6,
        margin: "8px 0 16px",
      },
      "& h4": {
        ...typography.subtitle2,
        margin: "16px 0 16px",
      },
      "& h5": {
        ...typography.subtitle2,
        margin: "32px 0 16px",
      },
      "& p, & ul, & ol": {
        marginTop: 0,
        marginBottom: 16,
      },
      "& ul": {
        paddingLeft: 30,
      },
      "& h1, & h2, & h3, & h4": {
        "& code": {
          fontSize: "inherit",
          lineHeight: "inherit",
          // Remove scroll on small screens.
          wordBreak: "break-all",
        },
        "& .anchor-link-style": {
          // To prevent the link to get the focus.
          display: "none",
        },
        "&:hover .anchor-link-style": {
          display: "inline-block",
          padding: "0 8px",
          color: palette.text.secondary,
          "&:hover": {
            color: palette.text.primary,
          },
          "& svg": {
            width: "0.7em",
            height: "0.7em",
            fill: "currentColor",
          },
        },
      },
      "& table": {
        // Trade display table for scroll overflow
        display: "block",
        wordBreak: "normal",
        width: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch", // iOS momentum scrolling.
        borderCollapse: "collapse",
        marginBottom: "16px",
        borderSpacing: 0,
        overflow: "hidden",
        "& .prop-name": {
          fontFamily: 'Consolas, "Liberation Mono", Menlo, monospace',
        },
        "& .required": {
          color: palette.type === "light" ? "#006500" : "#a5ffa5",
        },
        "& .prop-type": {
          fontFamily: 'Consolas, "Liberation Mono", Menlo, monospace',
          color: palette.type === "light" ? "#932981" : "#ffb6ec",
        },
        "& .prop-default": {
          fontFamily: 'Consolas, "Liberation Mono", Menlo, monospace',
          borderBottom: `1px dotted ${palette.divider}`,
        },
      },
      "& td": {
        ...typography.body2,
        borderBottom: `1px solid ${palette.divider}`,
        padding: 16,
        color: palette.text.primary,
      },
      "& td code": {
        lineHeight: 1.6,
      },
      "& th": {
        lineHeight: typography.pxToRem(24),
        fontWeight: typography.fontWeightMedium,
        color: palette.text.primary,
        whiteSpace: "pre",
        borderBottom: `1px solid ${palette.divider}`,
        padding: 16,
      },
      "& blockquote": {
        borderLeft: "5px solid #ffe564",
        backgroundColor: "rgba(255,229,100,0.2)",
        padding: "4px 24px",
        margin: "24px 0",
        "& p": {
          marginTop: "16px",
        },
      },
      "& a, & a code": {
        // Style taken from the Link component
        color: palette.secondary.main,
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
      },
      "& img, video": {
        maxWidth: "100%",
      },
      "& img": {
        // Avoid layout jump
        display: "inline-block",
      },
      "& hr": {
        height: 1,
        margin: spacing(6, 0),
        border: "none",
        flexShrink: 0,
        backgroundColor: palette.divider,
      },
      "& kbd": {
        // Style taken from GitHub
        padding: "2px 5px",
        font: "11px Consolas,Liberation Mono,Menlo,monospace",
        lineHeight: "10px",
        color: "#444d56",
        verticalAlign: "middle",
        backgroundColor: "#fafbfc",
        border: "1px solid #d1d5da",
        borderRadius: 3,
        boxShadow: "inset 0 -1px 0 #d1d5da",
      },
    },
  });

interface Props {
  className?: string;
  renderedMarkdown: string;
}

const useStyles = makeStyles(styles, { name: "MarkdownElement", flip: false });

const MarkdownElement = React.forwardRef(
  (
    { className, renderedMarkdown, ...other }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const classes = useStyles();
    const more = { dangerouslySetInnerHTML: { __html: "" } };

    if (typeof renderedMarkdown === "string") {
      // workaround for https://github.com/facebook/react/issues/17170
      // otherwise we could just set `dangerouslySetInnerHTML={undefined}`
      more.dangerouslySetInnerHTML = { __html: renderedMarkdown };
    }

    return (
      <div
        className={clsx(classes.root, "markdown-body", className)}
        {...more}
        {...other}
        ref={ref}
      />
    );
  }
);

export default MarkdownElement;
